import { Monster } from './Monster';
import { Earth } from '../earth/Earth';
import Phaser from 'phaser';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export class ShieldedAlien extends Monster {
  private shield: number = 2;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.health = 60;
    this.maxHealth = 60;
    this.speed = 28;
    this.reward = 15;
    this.specialAbility = 'Shield (blocks 2 hits)';

    // Visual representation
    const circle = scene.add.circle(0, 0, 8, 0x00bfff);
    circle.setName('monsterCircle');
    this.add(circle);
    // Shield visual
    const shieldCircle = scene.add.circle(0, 0, 11, 0x87ceeb, 0.4);
    shieldCircle.setName('shieldCircle');
    this.add(shieldCircle);
  }

  override update(time: number, delta: number): void {
    super.update(time, delta);
    if (!this.targetEarth) {
      this.findEarth();
    }
    if (this.targetEarth) {
      this.move();
    }
  }


  override move(): void {
    if (!this.targetEarth) return;
    const direction = new Phaser.Math.Vector2(
      this.targetEarth.x - this.x,
      this.targetEarth.y - this.y
    ).normalize();
    this.x += direction.x * this.speed * 0.016;
    this.y += direction.y * this.speed * 0.016;
    const distance = Phaser.Math.Distance.Between(this.x, this.y, this.targetEarth.x, this.targetEarth.y);
    if (distance < 60) {
      this.attackEarth();
    }
  }

  override attackEarth(): void {
    if (!this.scene) return;

    const _this = this;
    if (!this.targetEarth) return;
    
    // Create shield discharge effect
    this.createShieldDischargeEffect();
    
    // Shielded aliens deal moderate damage
    const baseDamage = 12;
    if (this.isActive) this.targetEarth.takeDamage(baseDamage);
    
    // Play attack sound
    const audioManager = AudioManager.getInstance(this.scene);
    audioManager.playSFX(SoundEffect.EARTH_DAMAGE);
    
    // Mark as inactive before destroying
    this.isActive = false;
    
    // Shield overload explosion
    this.createShieldOverloadExplosion();
    
    this.scene.time.delayedCall(120, () => {
      _this.destroy();
    });
  }

  private createShieldDischargeEffect(): void {
    if (!this.scene) return;
    // Electric discharge from shield
    for (let i = 0; i < 6; i++) {
      const lightning = this.scene.add.graphics();
      lightning.lineStyle(2, 0x4444ff, 0.8);
      
      // Create jagged lightning bolt
      const startX = this.x;
      const startY = this.y;
      const endX = this.targetEarth!.x + (Math.random() - 0.5) * 40;
      const endY = this.targetEarth!.y + (Math.random() - 0.5) * 40;
      
      lightning.beginPath();
      lightning.moveTo(startX, startY);
      
      // Add zigzag pattern
      const segments = 4;
      for (let j = 1; j <= segments; j++) {
        const t = j / segments;
        const x = startX + (endX - startX) * t + (Math.random() - 0.5) * 15;
        const y = startY + (endY - startY) * t + (Math.random() - 0.5) * 15;
        lightning.lineTo(x, y);
      }
      
      lightning.strokePath();
      
      // Fade out lightning
      this.scene.tweens.add({
        targets: lightning,
        alpha: 0,
        duration: 200,
        delay: i * 30,
        ease: 'Power2',
        onComplete: () => lightning.destroy()
      });
    }
    
    // Shield energy pulse
    const pulse = this.scene.add.circle(this.x, this.y, 25, 0x4444ff, 0.3);
    this.scene.tweens.add({
      targets: pulse,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => pulse.destroy()
    });
  }

  private createShieldOverloadExplosion(): void {
    // Blue electric explosion
    const explosion = this.scene.add.circle(this.x, this.y, 10, 0x6666ff, 0.9);
    this.scene.tweens.add({
      targets: explosion,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => explosion.destroy()
    });
    
    // Electric sparks
    for (let i = 0; i < 8; i++) {
      const spark = this.scene.add.rectangle(this.x, this.y, 1, 8, 0x8888ff);
      spark.setRotation((Math.PI * 2 * i) / 8);
      
      this.scene.tweens.add({
        targets: spark,
        alpha: 0,
        scaleY: 0,
        duration: 400,
        delay: i * 25,
        ease: 'Power2',
        onComplete: () => spark.destroy()
      });
    }
  }

  override activateAbility(): void {
    // Shielded alien: shield is passive
  }

  override takeDamage(amount: number): void {
    if (this.shield > 0) {
      this.shield--;
      this.showShieldEffect();
      // Remove shield visual if depleted
      if (this.shield === 0) {
        const shieldCircle = this.getByName('shieldCircle');
        if (shieldCircle instanceof Phaser.GameObjects.Graphics) {
          shieldCircle.setVisible(false);
        } else if (shieldCircle instanceof Phaser.GameObjects.Arc) {
          shieldCircle.setFillStyle(0x87ceeb, 0.4);
        }
      }
      return;
    }
    super.takeDamage(amount);
    this.showDamageEffect();
  }

  private showShieldEffect(): void {
    const shieldCircle = this.getByName('shieldCircle');
    if (shieldCircle instanceof Phaser.GameObjects.Graphics) {
      shieldCircle.clear();
      shieldCircle.fillStyle(0xffffff, 0.7);
      shieldCircle.fillCircle(0, 0, 11);
      this.scene.time.delayedCall(100, () => {
        shieldCircle.clear();
        shieldCircle.fillStyle(0x87ceeb, 0.4);
        shieldCircle.fillCircle(0, 0, 11);
      });
    } else if (shieldCircle instanceof Phaser.GameObjects.Arc) {
      shieldCircle.setFillStyle(0xffffff, 0.7);
      this.scene.time.delayedCall(100, () => {
        shieldCircle.setFillStyle(0x87ceeb, 0.4);
      });
    }
  }

  private showDamageEffect(): void {
    const circle = this.getByName('monsterCircle');
    if (circle instanceof Phaser.GameObjects.Arc) {
      circle.setFillStyle(0xffffff);
      this.scene.time.delayedCall(100, () => {
        circle.setFillStyle(0x00bfff);
      });
    } else if (circle instanceof Phaser.GameObjects.Graphics) {
      circle.clear();
      circle.fillStyle(0xffffff);
      this.scene.time.delayedCall(100, () => {
        circle.fillStyle(0x00bfff);
      });
    }
  }
} 